import { useCallback } from "react";
import { getCsrfHeader } from "../lib/csrf";
import { ADMIN_QUERIES } from "../lib/adminQueries";
import { useCachedQuery } from "./useCachedQuery";

export type AdminFlagsStatus = "loading" | "ready" | "error";

// Mirrors FlagAdminView on the backend.
export type AdminFlag = {
  key: string;
  description: string;
  default: boolean;
  globalOverride: boolean | null;
  userOverrideCount: number;
};

// Mirrors FlagOverrideView on the backend.
export type AdminFlagOverride = {
  email: string;
  enabled: boolean;
  updatedAt: string;
  updatedBy: string;
};

export type UseAdminFeatureFlagsResult = {
  status: AdminFlagsStatus;
  flags: ReadonlyArray<AdminFlag>;
  errorMessage: string | null;
  refetch: () => Promise<void>;
  setGlobalOverride: (key: string, enabled: boolean) => Promise<void>;
  clearGlobalOverride: (key: string) => Promise<void>;
  listOverrides: (key: string) => Promise<ReadonlyArray<AdminFlagOverride>>;
  setUserOverride: (key: string, email: string, enabled: boolean) => Promise<void>;
  clearUserOverride: (key: string, email: string) => Promise<void>;
};

const ADMIN_PATH = "/api/admin/feature-flags";

const fetchFlags = async (signal: AbortSignal): Promise<ReadonlyArray<AdminFlag>> => {
  const res = await fetch(ADMIN_PATH, { credentials: "include", signal });
  if (!res.ok) throw new Error(`Failed to load feature flags (${res.status})`);
  return (await res.json()) as ReadonlyArray<AdminFlag>;
};

export const useAdminFeatureFlags = (): UseAdminFeatureFlagsResult => {
  const { status, data, error, refetch } = useCachedQuery<ReadonlyArray<AdminFlag>>({
    cacheResource: ADMIN_QUERIES.featureFlags.resource,
    queryKey: ADMIN_PATH,
    fetcher: fetchFlags,
  });
  const flags = data ?? [];
  const errorMessage = error;

  const setGlobalOverride = useCallback(
    async (key: string, enabled: boolean): Promise<void> => {
      const res = await fetch(`${ADMIN_PATH}/${encodeURIComponent(key)}/global`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getCsrfHeader() },
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) throw new Error(`set global override failed (${res.status})`);
      await refetch();
    },
    [refetch],
  );

  const clearGlobalOverride = useCallback(
    async (key: string): Promise<void> => {
      const res = await fetch(`${ADMIN_PATH}/${encodeURIComponent(key)}/global`, {
        method: "DELETE",
        credentials: "include",
        headers: { ...getCsrfHeader() },
      });
      if (!res.ok) throw new Error(`clear global override failed (${res.status})`);
      await refetch();
    },
    [refetch],
  );

  const listOverrides = useCallback(
    async (key: string): Promise<ReadonlyArray<AdminFlagOverride>> => {
      const res = await fetch(`${ADMIN_PATH}/${encodeURIComponent(key)}/overrides`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`list overrides failed (${res.status})`);
      return (await res.json()) as ReadonlyArray<AdminFlagOverride>;
    },
    [],
  );

  const setUserOverride = useCallback(
    async (key: string, email: string, enabled: boolean): Promise<void> => {
      const res = await fetch(
        `${ADMIN_PATH}/${encodeURIComponent(key)}/users/${encodeURIComponent(email)}`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json", ...getCsrfHeader() },
          body: JSON.stringify({ enabled }),
        },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `set user override failed (${res.status})`);
      }
      await refetch();
    },
    [refetch],
  );

  const clearUserOverride = useCallback(
    async (key: string, email: string): Promise<void> => {
      const res = await fetch(
        `${ADMIN_PATH}/${encodeURIComponent(key)}/users/${encodeURIComponent(email)}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: { ...getCsrfHeader() },
        },
      );
      if (!res.ok) throw new Error(`clear user override failed (${res.status})`);
      await refetch();
    },
    [refetch],
  );

  return {
    status,
    flags,
    errorMessage,
    refetch,
    setGlobalOverride,
    clearGlobalOverride,
    listOverrides,
    setUserOverride,
    clearUserOverride,
  };
};
